import "../../../assets/css/PremiumTools.css";
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuthState } from "react-firebase-hooks/auth";
import {
  Copy,
  Youtube,
  Video,
  Hash,
  FileText,
  Zap,
  List,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import auth from "../../../firebase.init";

const YouTubeGenerator = () => {
  // ==========================================
  // 1. STATE MANAGEMENT
  // ==========================================
  const [user, loadingAuth] = useAuthState(auth);
  const [loading, setLoading] = useState(true);
  const [loadingKeys, setLoadingKeys] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  // User Data & Credits
  const [credits, setCredits] = useState(0);
  const [apiKeys, setApiKeys] = useState([]);

  // Form State
  const [videoTopic, setVideoTopic] = useState("");
  const [keywords, setKeywords] = useState("");
  const [videoType, setVideoType] = useState("Tutorial/How-To");
  const [tone, setTone] = useState("Engaging & Fast-Paced");

  // Output State
  const [results, setResults] = useState({
    titles: [],
    description: "",
    tags: [],
    scriptOutline: "",
  });

  // Options
  const videoTypes = [
    "Tutorial/How-To",
    "Product Review",
    "Vlog/Lifestyle",
    "Educational/Explainer",
    "Commentary/Opinion",
    "Gaming",
    "Shorts (Vertical)",
  ];

  const toneOptions = [
    "Engaging & Fast-Paced",
    "Professional & Authoritative",
    "Clickbait/Hype",
    "Relaxed & Chill",
    "Funny & Witty",
    "Inspirational",
  ];

  // ==========================================
  // 2. BACKEND / CREDIT LOGIC (UNCHANGED)
  // ==========================================

  const fetchApiKeys = async () => {
    try {
      const response = await axios.get(`https://allinone-clone-7d5d795e98ce.herokuapp.com/api/gemini-keys`);
      if (response.data && response.data.success && response.data.data) {
        const keys = response.data.data.map((keyObj) => keyObj.key);
        setApiKeys(keys);
      } else {
        setError("Failed to load API keys");
      }
    } catch (err) {
      console.error("Error fetching API keys:", err);
      setError("Error loading API keys");
    } finally {
      setLoadingKeys(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (loadingAuth) return;
      if (!user) {
        setError("User not logged in");
        setLoading(false);
        setLoadingKeys(false);
        return;
      }
      try {
        await fetchApiKeys();
        const userResponse = await axios.get(`https://allinone-clone-7d5d795e98ce.herokuapp.com/users`, {
          params: { userEmail: user.email },
        });
        if (userResponse.data && userResponse.data.length > 0) {
          setCredits(userResponse.data[0].currentBalance);
        }
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, loadingAuth]);

  const getRandomApiKey = () => {
    if (apiKeys.length === 0) throw new Error("No API keys available");
    return apiKeys[Math.floor(Math.random() * apiKeys.length)];
  };

  // ==========================================
  // 3. AI GENERATION LOGIC
  // ==========================================

  const constructPrompt = () => {
    return `You are a YouTube Growth Expert (MrBeast style retention + SEO specialist).
    Generate a complete video metadata kit for:
    
    Topic: ${videoTopic}
    Keywords: ${keywords}
    Video Type: ${videoType}
    Tone: ${tone}

    Please generate the following 4 sections exactly as requested:

    1. **===TITLES===**
    Generate 5 high-CTR (Click Through Rate) titles. Mix of clickbait and SEO. 
    Format: Just the titles, one per line.

    2. **===DESCRIPTION===**
    Write a 150-200 word SEO-optimized description.
    Include the keywords naturally.
    Include a placeholder section for "Timestamps" and "Links".
    End with a CTA (Call to Action).

    3. **===TAGS===**
    Generate 20 viral, high-volume search tags separated by commas.

    4. **===SCRIPT===**
    Write a brief bullet-point outline (Hook -> Value Prop -> Body Steps -> Conclusion).

    Ensure you use the delimiters ===TITLES===, ===DESCRIPTION===, ===TAGS===, and ===SCRIPT=== to separate sections.`;
  };

  const parseResponse = (text) => {
    const sections = {
      titles: [],
      description: "",
      tags: [],
      scriptOutline: "",
    };

    // Helper regex to extract content between delimiters
    const extract = (key) => {
      const regex = new RegExp(
        `===${key}===([\\s\\S]*?)(?=(===[A-Z]+===|$))`,
        "i",
      );
      const match = text.match(regex);
      return match ? match[1].trim() : "";
    };

    const rawTitles = extract("TITLES");
    sections.titles = rawTitles
      .split("\n")
      .filter((t) => t.trim().length > 0)
      .map((t) => t.replace(/^\d+\.\s*/, "").replace(/"/g, ""));

    sections.description = extract("DESCRIPTION");

    const rawTags = extract("TAGS");
    sections.tags = rawTags
      .split(",")
      .map((t) => t.trim())
      .filter((t) => t.length > 0);

    sections.scriptOutline = extract("SCRIPT");

    return sections;
  };

  const generateContent = async () => {
    if (!videoTopic.trim()) {
      toast.error("Please enter a video topic");
      return;
    }
    if (credits < 1) {
      setError("Insufficient credits.");
      return;
    }

    setGenerating(true);
    setError("");
    setResults({ titles: [], description: "", tags: [], scriptOutline: "" });

    try {
      // 1. Deduct Credit
      const deductRes = await axios.post(
        `https://allinone-clone-7d5d795e98ce.herokuapp.com/api/deduct-credit`,
        {
          userEmail: user.email,
          amount: 1,
        },
      );

      if (!deductRes.data.success) throw new Error("Credit deduction failed");
      setCredits((prev) => prev - 1);

      // 2. AI Call
      const MODEL_ID = "gemini-2.5-flash-lite";
      const apiKey = getRandomApiKey();
      const API_URL = `https://generativelanguage.googleapis.com/v1beta/models/${MODEL_ID}:generateContent?key=${apiKey}`;

      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contents: [{ role: "user", parts: [{ text: constructPrompt() }] }],
          generationConfig: {
            temperature: 0.85,
            maxOutputTokens: 2048,
          },
        }),
      });

      if (!response.ok) throw new Error("AI Service Unavailable");

      const data = await response.json();
      const text = data.candidates?.[0]?.content?.parts?.[0]?.text;

      if (!text) throw new Error("No content generated");

      setResults(parseResponse(text));
      toast.success("YouTube Kit Generated!");
    } catch (err) {
      console.error(err);
      setError(err.message);
      toast.error(err.message);

      // Refund
      try {
        await axios.post(`https://allinone-clone-7d5d795e98ce.herokuapp.com/api/refund-credit`, {
          userEmail: user.email,
          amount: 1,
        });
        setCredits((prev) => prev + 1);
      } catch (e) {
        console.error("Refund failed", e);
      }
    } finally {
      setGenerating(false);
    }
  };

  // ==========================================
  // 4. UTILS
  // ==========================================

  const copyToClipboard = (text, label) => {
    navigator.clipboard
      .writeText(text)
      .then(() => toast.success(`${label} Copied!`));
  };

  // ==========================================
  // 5. RENDER
  // ==========================================

  if (loadingAuth || loading || loadingKeys) {
    return (
      <div className="loader-wrapper">
        <div className="spinner-border text-primary" role="status"></div>
        <p className="mt-3 text-muted fw-medium">Loading Creator Studio...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="auth-wrapper">
        <div className="auth-card text-center">
          <div className="mb-4 icon-box mx-auto bg-primary bg-opacity-10 text-primary rounded-circle d-flex align-items-center justify-content-center" style={{width: 60, height: 60}}>
            <Youtube size={30} />
          </div>
          <h2 className="fw-bold text-dark mb-2">Creator Studio Access</h2>
          <p className="text-muted mb-4">Login to start generating viral video metadata.</p>
          <button className="btn btn-primary btn-lg w-100 fw-semibold" onClick={() => (window.location.href = "/login")}>
            Login Now
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="premium-app-container">
     
      {/* Navbar */}
      <nav className="premium-navbar">
        <div className="container-xl d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-3">
            <button className="btn-glass-icon" onClick={() => window.history.back()}>&larr;</button>
            <span className="premium-brand">
              <div className="brand-glow-icon"><Youtube size={18} color="white" /></div>
              Video Kit Generator
            </span>
          </div>
          <div className="credit-pill-glass">
            <Zap size={14} className="text-warning" fill="currentColor" />
            <span>{credits} Credits</span>
            {credits < 3 && <a href="/user/select-package" className="buy-btn-neon">Add</a>}
          </div>
        </div>
      </nav>

      <main className="container-xl py-4 py-lg-5">
        <div className="row g-4">
          
          {/* LEFT: Editor Configuration */}
          <div className="col-lg-4">
            <div className="glass-card h-100">
              <div className="glass-card-header">
                <Video size={18} className="text-info" />
                <h5 className="mb-0 fw-semibold text-white">Video Details</h5>
              </div>

              <div className="p-4">
                <div className="mb-4 input-group-glass">
                  <label>Video Topic / Title Idea</label>
                  <textarea
                    value={videoTopic}
                    onChange={(e) => setVideoTopic(e.target.value)}
                    placeholder="e.g. How to bake sourdough bread for beginners..."
                    rows={3}
                  />
                </div>

                <div className="mb-4 input-group-glass">
                  <label>Target Keywords (SEO)</label>
                  <input
                    type="text"
                    value={keywords}
                    onChange={(e) => setKeywords(e.target.value)}
                    placeholder="e.g. sourdough, baking, cooking tutorial"
                  />
                </div>

                <div className="mb-4 input-group-glass">
                  <label>Video Type</label>
                  <select
                    value={videoType}
                    onChange={(e) => setVideoType(e.target.value)}
                  >
                    {videoTypes.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-4 input-group-glass">
                  <label>Tone</label>
                  <select value={tone} onChange={(e) => setTone(e.target.value)}>
                    {toneOptions.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>

                <button
                  className="btn-neon-primary w-100"
                  onClick={generateContent}
                  disabled={generating || credits < 1 || !videoTopic.trim()}
                >
                  {generating ? (
                    <>
                      <Zap className="spin me-2" size={18} /> Generating...
                    </>
                  ) : (
                    <>
                      <Zap size={18} className="me-2" fill="currentColor" /> Generate Video Kit (1 Credit)
                    </>
                  )}
                </button>

                {error && (
                  <div className="mt-3 error-glass">
                    <AlertCircle size={14} className="me-2" /> {error}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT: Output Panel */}
          <div className="col-lg-8">
            <div className="glass-card h-100 d-flex flex-column">
              <div className="glass-card-header justify-content-between">
                <div className="d-flex align-items-center gap-2">
                  <List size={18} className="text-info" />
                  <h5 className="mb-0 fw-semibold text-white">Generated Kit</h5>
                </div>
              </div>

              <div className="glass-card-body flex-grow-1 position-relative">
                {generating ? (
                  <div className="state-container">
                    <div className="neon-spinner mb-4"></div>
                    <h4 className="text-white fw-bold">Analyzing YouTube Algorithm...</h4>
                    <p className="text-light opacity-75">Crafting high-CTR titles and SEO descriptions.</p>
                  </div>
                ) : results.titles.length > 0 ? (
                  <div className="output-scroll p-4">
                    {/* TITLES CARD */}
                    <div className="meta-box-glass mb-4">
                      <div className="meta-box-header justify-content-between">
                        <span><List size={14} /> Viral Title Options</span>
                        <button onClick={() => copyToClipboard(results.titles.join("\n"), "All Titles")} className="btn-glass-sm">
                          <Copy size={14} className="me-1" /> Copy
                        </button>
                      </div>
                      <div className="meta-content-styled">
                        {results.titles.map((title, idx) => (
                          <div key={idx} className="d-flex justify-content-between align-items-center border-bottom border-secondary border-opacity-25 py-2">
                            <span><span className="text-muted me-2">#{idx + 1}</span> {title}</span>
                            <button className="btn-glass-sm" onClick={() => copyToClipboard(title, "Title")}>Copy</button>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* DESCRIPTION CARD */}
                    <div className="meta-box-glass mb-4">
                      <div className="meta-box-header justify-content-between">
                        <span><FileText size={14} /> SEO Description</span>
                        <button onClick={() => copyToClipboard(results.description, "Description")} className="btn-glass-sm">
                          <Copy size={14} className="me-1" /> Copy
                        </button>
                      </div>
                      <div className="meta-content-styled">
                        {results.description}
                      </div>
                    </div>

                    {/* TAGS CARD */}
                    <div className="meta-box-glass mb-4">
                      <div className="meta-box-header justify-content-between">
                        <span><Hash size={14} /> Video Tags ({results.tags.length})</span>
                        <button onClick={() => copyToClipboard(results.tags.join(", "), "Tags")} className="btn-glass-sm">
                          <Copy size={14} className="me-1" /> Copy
                        </button>
                      </div>
                      <div className="d-flex flex-wrap gap-2">
                        {results.tags.map((tag, i) => (
                          <span key={i} className="badge bg-secondary bg-opacity-25 text-light p-2">{tag}</span>
                        ))}
                      </div>
                    </div>

                    {/* SCRIPT CARD */}
                    <div className="meta-box-glass mb-4">
                      <div className="meta-box-header justify-content-between">
                        <span><CheckCircle size={14} /> Script Outline</span>
                        <button onClick={() => copyToClipboard(results.scriptOutline, "Script")} className="btn-glass-sm">
                          <Copy size={14} className="me-1" /> Copy
                        </button>
                      </div>
                      <div className="meta-content-styled">
                        {results.scriptOutline}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="state-container opacity-50">
                    <Video size={64} className="text-white opacity-25 mb-3" />
                    <h4 className="text-white mt-3">No Content Generated Yet</h4>
                    <p className="text-light opacity-75">Enter your video details on the left to generate a full YouTube metadata kit.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default YouTubeGenerator;
